import { useState, useEffect } from 'react';
import MonacoEditor from '@monaco-editor/react';
import ReactMarkdown from 'react-markdown';

export interface SectionEditProps {
  section: { id: string; title: string; content: string };
  onSave: (content: string, title?: string) => Promise<void>;
  onRewrite: (content: string) => Promise<{ content: string }>;
}

export default function SectionEditor({ section, onSave, onRewrite }: SectionEditProps) {
  console.log('SectionEditor received section:', section);
  
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(section.content || '');
  const [title, setTitle] = useState(section.title || '');
  const [loading, setLoading] = useState(false);
  const [operation, setOperation] = useState<'save' | 'rewrite' | null>(null);
  const [editingTitle, setEditingTitle] = useState(false);
  
  // Update the value when section content or title changes from props
  useEffect(() => {
    console.log('Section content changed in props:', section.content);
    setValue(section.content || '');
  }, [section.content]);

  useEffect(() => {
    console.log('Section title changed in props:', section.title);
    setTitle(section.title || '');
  }, [section.title]);

  const handleSave = async () => {
    setLoading(true);
    setOperation('save');
    try {
      await onSave(value, title);
      setEditing(false);
      setEditingTitle(false);
    } catch (error) {
      console.error('Error saving section:', error);
    } finally {
      setLoading(false);
      setOperation(null);
    }
  };

  const handleRewrite = async () => {
    setLoading(true);
    setOperation('rewrite');
    try {
      console.log('Sending content for rewrite:', value);
      const result = await onRewrite(value);
      console.log('Rewrite result:', result);
      
      // Update the editor content with the rewritten text
      if (result && result.content) {
        setValue(result.content);
        console.log('Updated editor with rewritten content:', result.content);
      } else {
        console.warn('Received unexpected rewrite result format:', result);
      }
    } catch (error) {
      console.error('Error rewriting section:', error);
    } finally {
      setLoading(false);
      setOperation(null);
    }
  };

  return (
    <div className="section-editor mb-8 border border-gray-200 rounded-lg overflow-hidden">
      <div className="flex justify-between items-center bg-gray-50 px-4 py-2 border-b border-gray-200">
        {!editingTitle ? (
          <div className="flex items-center">
            <h3 className="text-lg font-medium text-gray-800">{title}</h3>
            <button 
              onClick={() => setEditingTitle(true)} 
              className="ml-2 text-blue-500 hover:text-blue-700 focus:outline-none"
              title="Edit title"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
          </div>
        ) : (
          <div className="flex items-center flex-grow mr-2">
            <input 
              type="text" 
              value={title} 
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded-md text-lg font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
            <button 
              onClick={() => {
                setEditingTitle(false);
                handleSave();
              }} 
              className="ml-2 text-green-500 hover:text-green-700 focus:outline-none"
              title="Save title"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </button>
            <button 
              onClick={() => {
                setTitle(section.title);
                setEditingTitle(false);
              }} 
              className="ml-1 text-red-500 hover:text-red-700 focus:outline-none"
              title="Cancel"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}
        {!editing && !editingTitle && (
          <button 
            onClick={() => setEditing(true)} 
            className="text-blue-500 hover:text-blue-700 focus:outline-none"
            title="Edit section content"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
        )}
      </div>
      <div className="p-4">
      {!editing ? (
        <div className="markdown-preview prose max-w-none">
          {value ? (
            <ReactMarkdown>{value}</ReactMarkdown>
          ) : (
            <p className="text-gray-500 italic">No content available for this section. Click Edit to add content.</p>
          )}
        </div>
      ) : (
        <div>
          <MonacoEditor
            height="250px"
            defaultLanguage="markdown"
            value={value}
            onChange={(val) => setValue(val ?? '')}
            options={{
              minimap: { enabled: false },
              wordWrap: 'on',
              fontSize: 16,
              scrollBeyondLastLine: false,
              lineNumbers: 'on',
              scrollbar: {
                vertical: 'visible',
                horizontal: 'visible',
              },
            }}
          />
          <div className="markdown-preview border-t mt-4 pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Preview:</h4>
            <div className="prose max-w-none border-l-4 border-blue-100 pl-4">
              <ReactMarkdown>{value}</ReactMarkdown>
            </div>
          </div>
        </div>
      )}
      <div className="flex gap-2 mt-4">
        {!editing ? (
          <button 
            className="inline-flex items-center bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors" 
            onClick={() => setEditing(true)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Edit Section
          </button>
        ) : (
          <div className="flex flex-wrap gap-2">
            <button 
              className="inline-flex items-center bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors" 
              onClick={handleSave} 
              disabled={loading}
            >
              {loading && operation === 'save' ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Save Changes
                </>
              )}
            </button>
            <button 
              className="inline-flex items-center bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors" 
              onClick={handleRewrite} 
              disabled={loading}
            >
              {loading && operation === 'rewrite' ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Rewriting...
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                  Rewrite with AI
                </>
              )}
            </button>
            <button 
              className="inline-flex items-center bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-md text-sm font-medium transition-colors" 
              onClick={() => setEditing(false)}
              disabled={loading}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
    </div>
  );
}
